import React, { useEffect, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Container, Form, Button, Image } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";

function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [contentPreview, setContentPreview] = useState("");
  const [cover, setCover] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  // Charger une news existante si editId
  useEffect(() => {
    const loadExisting = async () => {
      if (!editId) return;
      setLoading(true);
      try {
        const res = await fetch(`/backend/api/get_news_detail.php?id=${encodeURIComponent(editId)}`);
        const data = await res.json();
        if (data.success) {
          setTitle(data.news.title || '');
          setContentPreview(data.news.contentPreview || '');
          setContent(data.news.content || '');
          setIsPublic(!!data.news.is_public);
          setCoverPreview(data.news.cover || null);
        }
      } catch {}
      setLoading(false);
    };
    loadExisting();
  }, [editId]);

  const MAX_PREVIEW_LENGTH = 200;

  if (!user || user.role !== "admin") {
    navigate("/members-area");
    return null;
  }

  const handleContentPreviewChange = (e) => {
    const text = e.target.value;
    if (text.length <= MAX_PREVIEW_LENGTH) {
      setContentPreview(text);
    } else {
      setContentPreview(text.substring(0, MAX_PREVIEW_LENGTH));
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCover(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let coverUrl = null;

    if (cover) {
      const formData = new FormData();
      formData.append("file", cover);

      try {
        const uploadResponse = await fetch("/backend/admin/upload.php", {
          method: "POST",
          body: formData,
          credentials: "include",
        });

        const ct = uploadResponse.headers.get('content-type') || '';
        let uploadData;
        if (ct.includes('application/json')) {
          uploadData = await uploadResponse.json();
        } else {
          const uploadText = await uploadResponse.text();
          console.error("Réponse non-JSON (upload):", uploadText);
          alert("Erreur: réponse non-JSON lors de l'upload");
          return;
        }

        if (!uploadData.success) {
          alert("Erreur lors de l'upload de l'image de couverture: " + (uploadData.message || "inconnue"));
          return;
        }
        coverUrl = uploadData.location;
      } catch (error) {
        console.error("Erreur lors de l'upload de l'image de couverture:", error);
        alert("Erreur réseau ou parsing lors de l'upload de l'image de couverture.");
        return;
      }
    }

    const formData = new FormData();
    if (editId) { formData.append('id', editId); }
    formData.append("title", title);
    formData.append("contentPreview", contentPreview);
    formData.append("content", content);
    formData.append("is_public", isPublic ? 1 : 0);
    if (coverUrl) {
      formData.append("cover_url", coverUrl);
    }

    try {
      const endpoint = editId ? "/backend/admin/update_news.php" : "/backend/admin/add_news.php";
      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const ct = response.headers.get('content-type') || '';
      let data;
      if (ct.includes('application/json')) {
        data = await response.json();
      } else {
        const raw = await response.text();
        console.error("Réponse non-JSON (ajout/mise à jour):", raw);
        alert("Erreur: réponse non-JSON du serveur (voir console)");
        return;
      }
      if (data.success) {
        alert(editId ? "Article mis à jour !" : "Article ajouté avec succès !");
        setTitle("");
        setContentPreview("");
        setContent("");
        setCover(null);
        setCoverPreview(null);
        setIsPublic(false);
        if (editId) navigate('/');
      } else {
        alert(`Erreur serveur (ajout article): ${data.message}`);
      }
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'article:", error);
      alert("Une erreur s'est produite lors de l'ajout de l'article.");
    }
  };

  return (
    <Container className="mt-5 pb-5">
      <div className="mt-5 pb-5" />
      <div className="d-flex justify-content-end">
        <Button variant="outline-primary" size="sm" onClick={() => navigate('/admin/onboarding')}>
          Gérer l'onboarding des membres →
        </Button>
      </div>
      <h2>Ajouter une Actualité</h2>
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Titre</Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Texte de l'aperçu</Form.Label>
          <Form.Control
            type="text"
            value={contentPreview}
            onChange={handleContentPreviewChange}
            maxLength={MAX_PREVIEW_LENGTH}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Contenu</Form.Label>
          <Editor
            apiKey="q6gcgkouncj5tqjx3e3iw6seieccdff40xo80l2t6zbqyorv"
            value={content}
            init={{
              height: 400,
              convert_urls: false,
              menubar: true,
              plugins: "image media link lists code",
              toolbar: "undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media | code",
              images_upload_url: "/backend/admin/tiny_mce_upload.php",
              image_class_list: [
                { title: "Responsive", value: "img-responsive" }
              ],
              image_dimensions: true, // Activer les dimensions personnaliséess
              images_upload_url: "/backend/admin/tiny_mce_upload.php",
              file_picker_types: 'file image media', // Autoriser fichiers, images, vidéos
              file_picker_callback: (callback, value, meta) => {
                if (meta.filetype === 'file' || meta.filetype === 'image' || meta.filetype === 'media') {
                  const input = document.createElement('input');
                  input.setAttribute('type', 'file');
                  input.setAttribute('accept', meta.filetype === 'media' ? 'video/mp4' : 'image/*,.pdf'); // mp4 pour vidéo
                  input.onchange = function () {
                    const file = this.files[0];
                    const formData = new FormData();
                    formData.append('file', file);

                    fetch('/backend/admin/tiny_mce_upload.php', {
                      method: 'POST',
                      body: formData,
                      credentials: 'include',
                    })
                      .then(response => response.json())
                      .then(data => {
                        if (data.success) {
                          if (meta.filetype === 'media') {
                            callback(data.location, { source2: [{ src: data.location, type: 'video/mp4' }] });
                          } else {
                            callback(data.location, { title: file.name });
                          }
                        } else {
                          alert('Erreur lors de l\'upload: ' + data.message);
                        }
                      })
                      .catch(error => console.error('Erreur upload:', error));
                  };
                  input.click();
                }
              }
            }}
            onEditorChange={setContent}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Image de couverture</Form.Label>
          <div>
            <input
              type="file"
              accept="image/*,.avif" // Ajout de .avif
              className="d-none"
              id="cover-upload"
              onChange={handleCoverChange}
            />
            <Button
              variant="outline-primary"
              onClick={() => document.getElementById("cover-upload").click()}
            >
              Upload Image
            </Button>
            {coverPreview && (
              <div className="mt-3">
                <Image
                  src={coverPreview}
                  alt="Aperçu de l'image de couverture"
                  fluid
                  style={{ maxHeight: "200px", borderRadius: "10px" }}
                />
              </div>
            )}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="Rendre cette actualité publique"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
          />
        </Form.Group>

        <Button variant="primary" type="submit">
          {editId ? 'Mettre à jour' : 'Publier'}
        </Button>
        {editId && (
          <Button 
            variant="outline-danger" 
            className="ms-2"
            onClick={async () => {
              if (!window.confirm('Confirmer la suppression de cette actualité ?')) return;
              try {
                const fd = new FormData();
                fd.append('id', editId);
                const res = await fetch('/backend/admin/delete_news.php', { method: 'POST', body: fd, credentials: 'include' });
                const data = await res.json();
                if (data.success) { alert('Actualité supprimée'); navigate('/'); }
                else { alert('Erreur: ' + (data.message || 'suppression')); }
              } catch {
                alert('Erreur réseau lors de la suppression');
              }
            }}
          >
            Supprimer
          </Button>
        )}
      </Form>
    </Container>
  );
}

export default AdminPanel;